import Swal from "sweetalert2";
import "../css/dialogos_cartera.css";
import { convertirDireccion } from "../../functions/importar_cartera";

// Ventanas de la cartera: estilo Apple, iconos de Bootstrap y textos que explican
// en cada momento qué está pasando.

export const escapar = (t) =>
  String(t ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const formatearMoneda = (valor) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(valor || 0);

const formatearFecha = (iso) => iso.split("-").reverse().join("/");

const CLASES = {
  popup: "cartera-popup",
  htmlContainer: "cartera-contenido",
  actions: "cartera-acciones",
  confirmButton: "cartera-boton cartera-boton-principal",
  denyButton: "cartera-boton cartera-boton-secundario",
  cancelButton: "cartera-boton cartera-boton-secundario",
  closeButton: "cartera-cerrar",
  validationMessage: "cartera-validacion",
};

const encabezado = ({ icono, tono = "rojo", paso, titulo, texto }) => `
  <div class="cartera-encabezado">
    <div class="cartera-icono cartera-icono-${tono}"><i class="bi ${icono}"></i></div>
    <div class="cartera-encabezado-textos">
      ${paso ? `<p class="cartera-paso">${paso}</p>` : ""}
      <h2 class="cartera-titulo">${titulo}</h2>
      ${texto ? `<p class="cartera-texto">${texto}</p>` : ""}
    </div>
  </div>`;

const conIcono = (icono, texto) => `<i class="bi ${icono}"></i><span>${texto}</span>`;

export const nota = (tipo, icono, texto) =>
  `<div class="cartera-nota cartera-nota-${tipo}"><i class="bi ${icono}"></i><div>${texto}</div></div>`;

// Agrupa notas; en la ventana amplia de computador quedan lado a lado.
export const notas = (...lista) => {
  const visibles = lista.filter(Boolean);
  return visibles.length ? `<div class="cartera-notas">${visibles.join("")}</div>` : "";
};

const clases = (amplio) => ({
  ...CLASES,
  popup: `cartera-popup ${amplio ? "cartera-amplio" : "cartera-compacto"}`,
  container: amplio ? "cartera-contenedor cartera-contenedor-amplio" : "cartera-contenedor",
});

// Ventana base. Devuelve el resultado de Swal (isConfirmed, isDenied, dismiss, value).
// amplio: para los pasos del asistente (ancha en computador, hoja desde abajo en celular);
// si no, es compacta como una alerta.
export const dialogo = ({ icono, tono, paso, titulo, texto, contenido = "", confirmar = "Entendido", iconoConfirmar,
  cancelar, cerrar = false, amplio = false, ...opciones }) =>
  Swal.fire({
    html: encabezado({ icono, tono, paso, titulo, texto }) + contenido,
    confirmButtonText: iconoConfirmar ? conIcono(iconoConfirmar, confirmar) : confirmar,
    showCancelButton: Boolean(cancelar),
    cancelButtonText: cancelar,
    showCloseButton: cerrar,
    closeButtonAriaLabel: "Cerrar",
    reverseButtons: true,
    buttonsStyling: false,
    customClass: clases(amplio),
    ...opciones,
  });

// Ventana de espera sin botones; se reemplaza sola al abrir la siguiente.
export const mostrarEspera = (titulo, texto) => {
  Swal.fire({
    html: `<div class="cartera-espera">
             <div class="cartera-spinner"></div>
             <h2 class="cartera-titulo">${titulo}</h2>
             <p class="cartera-texto">${texto}</p>
           </div>`,
    showConfirmButton: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    customClass: clases(false),
  });
};

export const mostrarError = (titulo, texto) =>
  dialogo({ icono: "bi-exclamation-circle", tono: "naranja", titulo, texto });

// ---------- Asistente para cargar la cartera ----------

const TIPOS_VIVIENDA = {
  manzana_casa: {
    nombre: "Casas",
    detalle: "Manzana y casa",
    ejemplo: "C02 se carga como MANZANA C CASA 2",
    icono: "bi-house-door",
  },
  torre_apartamento: {
    nombre: "Apartamentos",
    detalle: "Torre y apartamento",
    ejemplo: "1-302 se carga como TORRE 1 APARTAMENTO 302",
    icono: "bi-building",
  },
};

// Ventana con tarjetas grandes para elegir una opción. Devuelve { valor } al continuar,
// { atras: true } si toca el botón secundario, o {} si cierra.
const elegirOpcion = async ({ opciones, elegido, validacion, cancelar = "Cancelar", ...resto }) => {
  const tarjetas = opciones.map(o => `
    <button type="button" class="cartera-opcion${o.clave === elegido ? " seleccionada" : ""}" data-clave="${o.clave}">
      <span class="cartera-opcion-icono"><i class="bi ${o.icono}"></i></span>
      <span class="cartera-opcion-textos">
        <span class="cartera-opcion-nombre">${o.nombre}</span>
        <span class="cartera-opcion-detalle">${o.detalle}</span>
      </span>
      <i class="bi ${o.clave === elegido ? "bi-check-circle-fill" : "bi-circle"} cartera-opcion-check"></i>
    </button>`).join("");

  let seleccion = elegido;
  const res = await dialogo({
    ...resto,
    amplio: true,
    contenido: `<div class="cartera-opciones">${tarjetas}</div>${resto.contenido || ""}`,
    confirmar: "Continuar",
    iconoConfirmar: "bi-arrow-right",
    cancelar,
    didOpen: (popup) => {
      popup.querySelectorAll(".cartera-opcion").forEach(boton => {
        boton.addEventListener("click", () => {
          seleccion = boton.dataset.clave;
          popup.querySelectorAll(".cartera-opcion").forEach(b => {
            const activa = b === boton;
            b.classList.toggle("seleccionada", activa);
            b.querySelector(".cartera-opcion-check").className = `bi ${activa ? "bi-check-circle-fill" : "bi-circle"} cartera-opcion-check`;
          });
          Swal.resetValidationMessage();
        });
      });
    },
    preConfirm: () => {
      if (!seleccion) {
        Swal.showValidationMessage(validacion);
        return false;
      }
      return seleccion;
    },
  });
  if (res.isConfirmed) return { valor: res.value };
  return res.dismiss === Swal.DismissReason.cancel ? { atras: true } : {};
};

const pasoTipoVivienda = async (elegido) => {
  const { valor } = await elegirOpcion({
    icono: "bi-houses",
    paso: "Paso 1 de 3",
    titulo: "¿Cómo son las viviendas del conjunto?",
    texto: "Con esto sabremos cómo leer la dirección de cada inmueble que viene en el archivo.",
    opciones: Object.entries(TIPOS_VIVIENDA).map(([clave, t]) => ({
      clave, icono: t.icono, nombre: t.nombre, detalle: `${t.detalle}. Ejemplo: ${t.ejemplo}.`,
    })),
    elegido,
    validacion: "Toque Casas o Apartamentos para continuar.",
  });
  return valor || null;
};

// Solo aparece si el archivo trae códigos como "L01": pueden ser locales o viviendas de la manzana/torre L.
const pasoCodigosL = async (tipo, codigos) => {
  const ejemplo = codigos[0];
  const lista = codigos.slice(0, 6).map(escapar).join(", ") + (codigos.length > 6 ? "…" : "");
  const { valor, atras } = await elegirOpcion({
    icono: "bi-shop",
    paso: "Una pregunta más",
    titulo: `¿Qué son los inmuebles que empiezan con L?`,
    texto: `En el archivo hay ${codigos.length} ${codigos.length === 1 ? "código" : "códigos"} así: <strong>${lista}</strong>.`,
    opciones: [
      { clave: "local", icono: "bi-shop", nombre: "Locales comerciales",
        detalle: `${escapar(ejemplo)} se carga como ${escapar(convertirDireccion(ejemplo, tipo, "local"))}.` },
      { clave: "vivienda", icono: TIPOS_VIVIENDA[tipo].icono, nombre: TIPOS_VIVIENDA[tipo].nombre,
        detalle: `${escapar(ejemplo)} se carga como ${escapar(convertirDireccion(ejemplo, tipo, "vivienda"))}.` },
    ],
    validacion: "Toque Locales comerciales o " + TIPOS_VIVIENDA[tipo].nombre + " para continuar.",
    contenido: notas(nota("info", "bi-info-circle",
      "Si son locales, para que el dueño vea su cartera en la opción Pagos, su dirección en CoordiPH debe ser la misma, por ejemplo <strong>LOCAL 1</strong>.")),
    cancelar: "Atrás",
    cerrar: true,
  });
  if (valor) return valor;
  return atras ? "atras" : null;
};

// Abre el explorador de archivos. Devuelve el archivo o null si la persona lo cierra sin elegir.
const abrirExplorador = () => new Promise(resolve => {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".xlsx,.xls,.xlsm,.ods,.csv,.txt";
  input.onchange = () => resolve(input.files[0] || null);
  input.oncancel = () => resolve(null);
  input.click();
});

const pasoArchivo = async (tipo) => {
  const t = TIPOS_VIVIENDA[tipo];
  const res = await dialogo({
    icono: "bi-file-earmark-spreadsheet",
    paso: "Paso 2 de 3",
    titulo: "Seleccione el archivo de cartera",
    texto: "Puede ser un Excel (.xlsx o .xls) o un archivo CSV, tal como lo entrega el software contable.",
    contenido: notas(
      nota("info", t.icono, `Tipo de vivienda elegido: <strong>${t.nombre} (${t.detalle.toLowerCase()})</strong>. Si no es correcto, toque Atrás.`),
      nota("info", "bi-list-check",
        "El archivo debe tener una columna con el inmueble (por ejemplo <strong>TERCERO</strong> o <strong>INMUEBLE</strong>) y columnas con los valores: administración, retroactivo, intereses, seguro, multas y total.")),
    amplio: true,
    confirmar: "Buscar archivo",
    iconoConfirmar: "bi-folder2-open",
    cancelar: "Atrás",
    cerrar: true,
  });
  if (res.isConfirmed) return { archivo: await abrirExplorador() };
  return { atras: res.dismiss === Swal.DismissReason.cancel };
};

const contenidoRevision = (nombreArchivo, tipo, res) => {
  const registros = Object.values(res.datos);
  const total = registros.reduce((suma, r) => suma + (Number(r.total_cartera) || 0), 0);
  const t = TIPOS_VIVIENDA[tipo];
  const fila = (icono, etiqueta, valor) => `
    <div class="cartera-fila">
      <span class="cartera-fila-icono"><i class="bi ${icono}"></i></span>
      <span class="cartera-fila-etiqueta">${etiqueta}</span>
      <span class="cartera-fila-valor">${valor}</span>
    </div>`;
  const conversiones = res.ejemplos.map(e => `
    <div class="cartera-conversion">
      <span class="cartera-conversion-original">${escapar(e.original)}</span>
      <i class="bi bi-arrow-right cartera-conversion-flecha"></i>
      <span class="cartera-conversion-final">${escapar(e.direccion)}</span>
    </div>`).join("");

  return `
    <div class="cartera-columnas">
      <div>
        <p class="cartera-subtitulo">Resumen del archivo</p>
        <div class="cartera-lista">
          ${fila("bi-file-earmark-spreadsheet", "Archivo", escapar(nombreArchivo))}
          ${fila(t.icono, "Tipo de vivienda", t.nombre)}
          ${fila("bi-list-ol", "Inmuebles encontrados", registros.length)}
          ${fila("bi-calendar-date", "Fecha de corte", formatearFecha(res.fechaCorte))}
          ${fila("bi-cash-stack", "Total cartera", formatearMoneda(total))}
        </div>
      </div>
      <div>
        <p class="cartera-subtitulo">Así quedarán las direcciones</p>
        <div class="cartera-lista">${conversiones}</div>
      </div>
    </div>
    ${notas(
      !res.fechaEncontrada &&
        nota("aviso", "bi-calendar-x", `El archivo no dice la fecha de corte. Se usará la de hoy: <strong>${formatearFecha(res.fechaCorte)}</strong>.`),
      res.omitidas.length > 0 &&
        nota("aviso", "bi-exclamation-triangle",
          `Estas filas no tienen un inmueble y no se cargarán: <strong>${res.omitidas.slice(0, 4).map(escapar).join(", ")}${res.omitidas.length > 4 ? "…" : ""}</strong>.`),
      res.columnasIgnoradas.length > 0 &&
        nota("aviso", "bi-layout-three-columns",
          `No se muestran por separado, pero sí van incluidas en el total: <strong>${res.columnasIgnoradas.map(escapar).join(", ")}</strong>.`),
      nota("info", "bi-info-circle",
        "Cada residente verá estos valores en <strong>Pagos</strong>. Si un inmueble ya tenía cartera, se reemplaza. Los que no vienen en el archivo quedan como estaban."),
    )}`;
};

const pasoRevision = async (nombreArchivo, tipo, res) => {
  const r = await dialogo({
    icono: "bi-clipboard-check",
    paso: "Paso 3 de 3",
    titulo: "Revise antes de cargar",
    texto: "Compare estos datos con su archivo. Si algo no coincide, toque Atrás y elija otro archivo.",
    contenido: contenidoRevision(nombreArchivo, tipo, res),
    amplio: true,
    confirmar: "Cargar cartera",
    iconoConfirmar: "bi-cloud-arrow-up",
    cancelar: "Atrás",
    cerrar: true,
  });
  if (r.isConfirmed) return "cargar";
  return r.dismiss === Swal.DismissReason.cancel ? "atras" : "salir";
};

const problemaConArchivo = async (titulo, texto) => {
  const r = await dialogo({
    icono: "bi-exclamation-circle", tono: "naranja", titulo, texto,
    confirmar: "Elegir otro archivo", iconoConfirmar: "bi-folder2-open",
    cancelar: "Cerrar",
  });
  return r.isConfirmed;
};

// Lleva a la persona paso a paso: tipo de vivienda, archivo, revisión y guardado.
// leer(archivo, tipo, codigosL) devuelve el resultado de leerArchivoCartera; guardar(datos) devuelve { exito, mensaje }.
// Devuelve true si se guardó la cartera.
export const asistenteCargarCartera = async ({ leer, guardar }) => {
  let tipo = null;
  let paso = "tipo";
  while (true) {
    if (paso === "tipo") {
      tipo = await pasoTipoVivienda(tipo);
      if (!tipo) return false;
      paso = "archivo";
      continue;
    }

    const { archivo, atras } = await pasoArchivo(tipo);
    if (atras) { paso = "tipo"; continue; }
    if (archivo === undefined) return false;
    if (archivo === null) continue;

    mostrarEspera("Leyendo el archivo", `Estamos revisando "${escapar(archivo.name)}". Esto toma unos segundos.`);
    let res;
    try {
      res = await leer(archivo, tipo);
    } catch (error) {
      if (await problemaConArchivo("No se pudo abrir el archivo",
        `Revise que "${escapar(archivo.name)}" sea un Excel o un CSV y que no esté dañado. Detalle: ${escapar(error.message)}`)) continue;
      return false;
    }
    if (!res.encontroEncabezado) {
      if (await problemaConArchivo("No encontramos las columnas de la cartera",
        `En "${escapar(archivo.name)}" falta una fila de títulos con el inmueble (TERCERO, INMUEBLE, DIRECCIÓN, APTO o CASA) y al menos un valor (ADMINISTRACIÓN, INTERESES, SEGURO, MULTAS o TOTAL).`)) continue;
      return false;
    }
    if (Object.keys(res.datos).length === 0) {
      if (await problemaConArchivo("El archivo no tiene inmuebles",
        `"${escapar(archivo.name)}" solo tiene la fila de títulos. Revise que debajo estén los datos de cada inmueble.`)) continue;
      return false;
    }

    if (res.codigosConL.length > 0) {
      const codigosL = await pasoCodigosL(tipo, res.codigosConL);
      if (codigosL === "atras") continue;
      if (!codigosL) return false;
      if (codigosL === "local") {
        mostrarEspera("Preparando los locales", "Estamos ajustando las direcciones de los locales.");
        res = await leer(archivo, tipo, "local");
      }
    }

    const decision = await pasoRevision(archivo.name, tipo, res);
    if (decision === "atras") continue;
    if (decision === "salir") return false;

    const cantidad = Object.keys(res.datos).length;
    mostrarEspera("Guardando la cartera", `Estamos cargando ${cantidad} inmuebles. No cierre esta página.`);
    const envio = await guardar(res.datos);
    if (!envio.exito) {
      await mostrarError("No se pudo guardar la cartera", `No se guardó ningún cambio. Intente de nuevo en unos minutos. Detalle: ${escapar(envio.mensaje)}`);
      return false;
    }
    await dialogo({
      icono: "bi-check-circle", tono: "verde",
      titulo: "Cartera cargada",
      texto: `Se cargaron <strong>${cantidad}</strong> inmuebles con corte al <strong>${formatearFecha(res.fechaCorte)}</strong>. Los residentes ya pueden consultarla en la opción Pagos.`,
      confirmar: "Listo",
    });
    return true;
  }
};
